import {
    Alert,
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography
} from "@mui/material";
import * as Sentry from "@sentry/react";
import {useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {useMutation} from "react-query";
import {useNavigate} from 'react-router-dom';
import {loginUser, resolveVerification} from "@/services/auth.ts";
import {LoginUserFormValues} from "@/types/utils.ts";
import {NavLink} from "react-router-dom";

import {vestResolver} from "@hookform/resolvers/vest";
import {grey} from "@mui/material/colors";
import validators from "./validators";

import {setToken} from "@/utils/functions.ts";
import DashedBreakline from "@/components/Shared/DashedBreakline";

const LoginForm = ({setVerification, setUncompleted}: any) => {
    const navigate = useNavigate();

    const {
        control,
        formState: {errors},
        handleSubmit,
    } = useForm({
        defaultValues: {
            email: "",
            password: "",
        },
        resolver: vestResolver(validators)
    });

    const [isLoading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>("");

    const {mutateAsync: loginMutate} = useMutation(loginUser);

    const handleLoginUser = async (values: LoginUserFormValues) => {
        setLoading(true);
        setErrorMessage(null);

        Sentry.setUser({ email: values.email });

        try {
            const response = await loginMutate(values);

            if (response?.errors && response?.errors?.length > 0) {
                if (response?.errors?.[0]?.code === 1401) {
                    const verify = await resolveVerification(values.email)

                    if (verify?.errors) {
                        setLoading(false)
                        setErrorMessage(verify?.errors?.[0].message)
                    }

                    if (verify?.data?.created) {
                        setLoading(false)

                        return [
                            setUncompleted(true),
                            setVerification(values.email)
                        ]
                    }
                }
                console.log(response)

                setLoading(false)
                return setErrorMessage(response?.errors?.[0]?.message)
            }

            const {
                mfa
            } = response?.data || {};

            if (mfa?.enable && mfa?.information) {
                const verify = await resolveVerification(mfa?.information)

                setLoading(false)

                if (verify?.errors) {
                    setErrorMessage(verify?.errors?.[0].message)
                }

                if (verify?.data?.created) {
                    return [
                        setVerification(mfa?.information)
                    ]
                }
            } else {
                const {access_token, exp, uuid} = response?.data || {};

                if (access_token && exp && uuid) {
                    setToken(access_token, exp, uuid);
                    navigate("/");
                } else {
                    setLoading(false);
                    setErrorMessage("Invalid response from server. Please try again.");
                }
            }
        } catch (exception: any) {
            setLoading(false)
            setErrorMessage(exception.message)
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit(handleLoginUser)}>
            {errorMessage && (
                <Alert severity="error" sx={{mb: 4, fontSize: 14}}>{errorMessage}</Alert>
            )}
            <Controller
                name="email"
                defaultValue=""
                control={control}
                render={({field}) => (
                    <TextField
                        {...field}
                        size="small"
                        type="text"
                        label="Email (*)"
                        error={!!errors.email}
                        InputLabelProps={{shrink: true}}
                        helperText={errors.email?.message}
                        sx={{minWidth: "100%", "& input": {fontSize: 13}}}
                    />
                )}
            />

            <Controller
                name="password"
                defaultValue=""
                control={control}
                render={({field}) => (
                    <TextField
                        {...field}
                        size="small"
                        type="password"
                        label="Password (*)"
                        error={!!errors?.password}
                        InputLabelProps={{shrink: true}}
                        helperText={errors?.password?.message}
                        sx={{minWidth: "100%", "& input": {fontSize: 13}, mt: 3}}
                    />
                )}
            />

            <Button
                disabled={isLoading}
                type="submit"
                sx={{mt: 3}}
                fullWidth
                variant="contained"
            >
                {isLoading ? (
                    <CircularProgress size={24} sx={{color: grey[900]}}/>
                ) : (
                    <Typography fontSize={14} fontWeight={700} sx={{textTransform: 'capitalize'}}>Login</Typography>
                )}
            </Button>

            <DashedBreakline text="New to VNETWORK"/>

            <Button component={NavLink} to="/auth/register" fullWidth
                    sx={{bgcolor: grey[50], color: 'black ! important'}}>
                <Typography sx={{fontSize: 14, fontWeight: 500, textTransform: 'capitalize'}}>
                    Sign Up Now
                </Typography>
            </Button>
        </Box>
    );
};

export default LoginForm;
